import React, { ReactElement, useMemo } from "react"
import { Box, Stack, useTheme } from "@carrotfertility/carotene-core"
import { useWatch } from "@carrotfertility/carotene-core-x"
import { Employee } from "types/Employee"
import { MemberJourneyData } from "types/MemberJourney"
import { CarrotPlanFormOption, MenuItemOptionFields, TopicOptionFields } from "./hooks/useCarrotPlanMappings"
import { FieldName } from "./CarrotPlanFormTypes"
import { useLastKnownOptions } from "./hooks/useLastKnownOptions"
import { useDynamicCarrotPlanMappings } from "./hooks/useDynamicCarrotPlanMappings"
import { useResetFormStepsOnJourneyChange } from "./hooks/useResetFormStepsOnJourneyChange"
import { JourneySelectSection, NONE_VALUE } from "./JourneySelection"
import JourneyChangeConfirmation from "./JourneyChangeConfirmation"
import { CarrotPlanStepsCarotene } from "./CarrotPlanStepsCarotene"
import { FormNavigation } from "./CarrotPlanFormNavigation"
import { AssistantRecommendationsPanel } from "./AdditionalRecommendations"
import { getOnlyRequiredMenuItems } from "./utils/autocompleteTransformations"

export type CarrotPlanFormContentWithDynamicOptionsProps = {
  employee: Employee
  benefitEnabledJourneyData: any
  inReview: boolean
  onBack: () => void
  onCancel: () => void
  initialMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  initialTopics: CarrotPlanFormOption<TopicOptionFields>[]
  showJourneyChangeConfirmation: boolean
  memberJourney: MemberJourneyData
  handleJourneyChangeConfirm: () => void
  handleJourneyChangeCancel: () => void
}

export function CarrotPlanFormContentWithDynamicOptions({
  employee,
  benefitEnabledJourneyData,
  inReview,
  onBack,
  onCancel,
  initialMenuItems,
  initialTopics,
  showJourneyChangeConfirmation,
  memberJourney,
  handleJourneyChangeConfirm,
  handleJourneyChangeCancel
}: CarrotPlanFormContentWithDynamicOptionsProps): ReactElement {
  const theme = useTheme()

  const selectedPrimaryJourney = useWatch({ name: FieldName.primaryJourney })
  const selectedPrimaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })

  const { menuItems, topics, isLoading } = useDynamicCarrotPlanMappings({
    employee,
    initialMenuItems,
    initialTopics,
    journey: selectedPrimaryJourney,
    stage: selectedPrimaryJourneyPhase
  })

  const { displayMenuItems, displayTopics } = useLastKnownOptions(menuItems, topics, isLoading)

  const bothJourneyAndStageSelected =
    selectedPrimaryJourney !== NONE_VALUE && selectedPrimaryJourneyPhase !== NONE_VALUE

  const newRequiredItems = useMemo(
    () => (bothJourneyAndStageSelected && !isLoading ? getOnlyRequiredMenuItems(menuItems) : null),
    [bothJourneyAndStageSelected, isLoading, menuItems]
  )

  useResetFormStepsOnJourneyChange({
    journey: selectedPrimaryJourney,
    stage: selectedPrimaryJourneyPhase,
    newRequiredItems
  })

  return (
    <Stack spacing={theme.tokens.spacing.xl}>
      <JourneySelectSection
        journeysWithStages={benefitEnabledJourneyData}
        employeeName={employee.preferredName ?? employee.firstName}
        inReview={inReview}
      />
      {showJourneyChangeConfirmation && (
        <JourneyChangeConfirmation
          memberJourney={memberJourney}
          benefitEnabledJourneyData={benefitEnabledJourneyData}
          onConfirm={handleJourneyChangeConfirm}
          onCancel={handleJourneyChangeCancel}
        />
      )}
      <Stack direction={"row"} spacing={theme.tokens.spacing.md} width={"100%"}>
        <Box
          flexDirection="column"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          display="flex"
          width="600px"
        >
          <CarrotPlanStepsCarotene
            employee={employee}
            inReview={inReview}
            stepOptions={displayMenuItems}
            topicOptions={displayTopics}
          />
          <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)} display="flex">
            <FormNavigation onBack={onBack} onCancel={onCancel} inReview={inReview} />
          </Box>
        </Box>
        {!inReview && (
          <AssistantRecommendationsPanel
            benefitEnabledJourneyData={benefitEnabledJourneyData}
            menuItemOptions={menuItems}
            topicOptions={topics}
            isFetchingOptions={isLoading}
          />
        )}
      </Stack>
    </Stack>
  )
}
